import { useMemo } from 'react'
import { Group, Rect, Text } from 'react-konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
  TEXT_PADDING_X,
  TEXT_PADDING_Y,
} from '../constants'
import { ShapeInteraction } from './shapeInteraction'
import { TEXT_CONTENT_NAME, measureTextBoxHeightPx } from './textLayout'
import { LinkActivation, findTextLinks } from './textLinks'
import LinkedText from './LinkedText'

type Props = {
  shape: Extract<VisualShape, { type: 'text' }>
  width: number
  height: number
  interaction?: ShapeInteraction
  isEditing?: boolean
  linkActivation?: LinkActivation
}

const TextShape = ({
  shape,
  width,
  height,
  interaction,
  isEditing = false,
  linkActivation,
}: Props) => {
  const boxWidth = shape.width * width
  const fontSize = shape.font_size * width
  const boxHeight = measureTextBoxHeightPx(shape.text, boxWidth, fontSize)
  const textWidth = Math.max(1, boxWidth - TEXT_PADDING_X * 2)
  const centerX = shape.x * width
  const centerY = shape.y * height
  const textColor = shape.fill_rgb ?? DEFAULT_TEXT_COLOR
  const backgroundColor = shape.stroke_rgb ?? DEFAULT_TEXT_BACKGROUND
  const text = shape.text || 'Text'
  const links = useMemo(() => findTextLinks(text), [text])
  const hasClickableLinks = links.length > 0 && linkActivation !== undefined

  return (
    <Group
      ref={interaction?.nodeRef}
      x={centerX}
      y={centerY}
      width={boxWidth}
      height={boxHeight}
      offsetX={boxWidth / 2}
      offsetY={boxHeight / 2}
      rotation={shape.rotation ?? 0}
      draggable={interaction?.draggable ?? false}
      listening={interaction !== undefined || hasClickableLinks}
      clipX={0}
      clipY={0}
      clipWidth={boxWidth}
      clipHeight={boxHeight}
      onMouseEnter={interaction?.onMouseEnter}
      onMouseLeave={interaction?.onMouseLeave}
      onMouseDown={interaction?.onMouseDown}
      onTap={interaction?.onTap}
      onDblClick={interaction?.onDblClick}
      onDblTap={interaction?.onDblClick}
      onDragStart={interaction?.onDragStart}
      onDragMove={interaction?.onDragMove}
      onDragEnd={interaction?.onDragEnd}
    >
      <Rect
        width={boxWidth}
        height={boxHeight}
        fill={backgroundColor}
        cornerRadius={4}
        visible={!isEditing}
        listening={interaction !== undefined}
      />
      {links.length > 0 ? (
        <LinkedText
          x={TEXT_PADDING_X}
          y={TEXT_PADDING_Y}
          width={textWidth}
          text={text}
          fontSize={fontSize}
          fill={textColor}
          visible={!isEditing}
          links={links}
          activation={linkActivation}
        />
      ) : (
        <Text
          name={TEXT_CONTENT_NAME}
          x={TEXT_PADDING_X}
          y={TEXT_PADDING_Y}
          width={textWidth}
          text={text}
          fontSize={fontSize}
          fontFamily={TEXT_FONT_FAMILY}
          lineHeight={TEXT_LINE_HEIGHT}
          fill={textColor}
          wrap='word'
          align='center'
          visible={!isEditing}
          listening={false}
        />
      )}
    </Group>
  )
}

export default TextShape
